import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';
import { processKeys } from '../query-keys';
import { useAuthStore } from '@/stores/auth-store';
import {
  listAllProcessesContract,
  showProcessContract,
  exportProcessContract,
  type ProcessListRow,
  type ListAllProcessesResponse,
  type ShowProcessResponse,
  type ExportProcessQuery
} from '@bliks-io/contracts';

export const processQueryOptions = (id: string) =>
  queryOptions({
    queryKey: processKeys.detail(id),
    queryFn: async ({ signal }): Promise<ShowProcessResponse> => {
      const { data } = await apiClient.get<ShowProcessResponse>(
        showProcessContract.path.replace(':id', id),
        { signal }
      );
      return data;
    }
  });

/**
 * Fetch processes list
 *
 * @example
 * ```tsx
 * function ProcessesList() {
 *   const { data: processes, isLoading } = useProcesses();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!processes) return null;
 *
 *   return <List items={processes} />;
 * }
 * ```
 */
export function useProcesses() {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: processKeys.list(),
    queryFn: async ({ signal }): Promise<ProcessListRow[]> => {
      const { data } = await apiClient.get<ListAllProcessesResponse>(
        listAllProcessesContract.path,
        { signal }
      );
      return data.processes;
    },
    enabled: !!token
  });
}

/**
 * Fetch a single process by ID with step recordings and pending assignments
 *
 * @example
 * ```tsx
 * function ProcessDetail({ id }: { id: string }) {
 *   const { data, isLoading } = useProcess(id);
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!data) return <NotFound />;
 *
 *   return <DetailView process={data.process} assignments={data.pendingAssignments} />;
 * }
 * ```
 */
export function useProcess(id: string) {
  const { token } = useAuthStore();

  return useQuery({
    ...processQueryOptions(id),
    enabled: !!token && !!id
  });
}

/**
 * Export a single process as a downloadable JSON or YAML blob. The flags select
 * which sections to include, whether references are populated or bare ids,
 * whether to embed org context, and the output format.
 *
 * @example
 * ```tsx
 * const exportProcess = useExportProcess(processId);
 * const blob = await exportProcess.mutateAsync({
 *   includeSteps: true,
 *   includeAssignments: true,
 *   includeCapacity: false,
 *   includeContext: false,
 *   format: 'json',
 * });
 * ```
 */
export function useExportProcess(processId: string) {
  return useMutation({
    mutationFn: async (opts: ExportProcessQuery): Promise<Blob> => {
      const { data } = await apiClient.get<Blob>(
        exportProcessContract.path.replace(':id', processId),
        { params: opts, responseType: 'blob' }
      );
      return data;
    }
  });
}
